import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

/**
 * Batch 18 §11.3 (Features__PhoneSearch): whether the phone search bar is open. Open is a flag in the history entry's state,
 * so the browser's Back closes the bar instead of leaving the page. Opening pushes an entry for the same address (the page
 * keeps its scroll, see historyScroll); a search first drops the flag from that entry, so Back from the results returns to
 * the page with the bar closed.
 */
const OPEN_KEY = "gfPhoneSearch";

function stateRecord(state: unknown): Record<string, unknown> {
  return state !== null && typeof state === "object" && !Array.isArray(state) ? (state as Record<string, unknown>) : {};
}

export interface PhoneSearchControl {
  open: boolean;
  openBar: () => void;
  /** Cancel, Esc: back out of the entry the bar opened, or (after a reload) drop the flag in place. */
  closeBar: () => void;
  /** Before a search navigates away: the current entry forgets the bar, without moving. */
  closeForSearch: () => void;
  /** True once, right after the bar closed because a search was made (focus then goes to the page, not to the icon). */
  takeClosedBySearch: () => boolean;
}

export function usePhoneSearch(enabled: boolean): PhoneSearchControl {
  const location = useLocation();
  const navigate = useNavigate();
  const pushed = useRef(false);
  const bySearch = useRef(false);
  const state = stateRecord(location.state);
  const open = enabled && state[OPEN_KEY] === true;
  const here = `${location.pathname}${location.search}${location.hash}`;
  const without = () => {
    const rest = Object.fromEntries(Object.entries(state).filter(([key]) => key !== OPEN_KEY));
    return Object.keys(rest).length > 0 ? rest : null;
  };
  useEffect(() => {
    if (!open) pushed.current = false;
  }, [open]);
  return {
    open,
    openBar: () => {
      if (open) return;
      pushed.current = true;
      void navigate(here, { state: { ...state, [OPEN_KEY]: true } });
    },
    closeBar: () => {
      if (!open) return;
      if (pushed.current) void navigate(-1);
      else void navigate(here, { replace: true, state: without() });
    },
    closeForSearch: () => {
      if (!open) return;
      bySearch.current = true;
      void navigate(here, { replace: true, state: without() });
    },
    takeClosedBySearch: () => {
      const was = bySearch.current;
      bySearch.current = false;
      return was;
    },
  };
}
